<script setup>
import { computed } from 'vue';
import { prefersReducedMotion } from 'dashboard/composables/useFocusMotion';
import { SOLAR_ICONS } from './solarIcons';

// Identidade Visor: ícone Solar Bold Duotone em SVG na página, e não como
// classe `i-*` do @egoist/tailwindcss-icons. O plugin achata o SVG numa
// máscara CSS, e máscara não deixa mexer numa parte do desenho. Aqui as duas
// camadas (a de opacidade .5 e a cheia) ficam em <g> separados, e só a peça
// (porta, badalo, bolinhas) anima: uma vez por hover no item que tem a classe
// `group/solar`, ou quando o item fica ativo. Só transform, ~220 ms, e nada
// anima com prefers-reduced-motion.
const props = defineProps({
  name: {
    type: String,
    required: true,
    validator: value => Boolean(SOLAR_ICONS[value]),
  },
  active: { type: Boolean, default: false },
});

// Classes inteiras (não montadas por concatenação) para o Tailwind gerar.
const MOTIONS = {
  slide: {
    hover: 'group-hover/solar:animate-solar-slide',
    active: 'animate-solar-slide',
    origin: 'origin-center',
  },
  lift: {
    hover: 'group-hover/solar:animate-solar-lift',
    active: 'animate-solar-lift',
    origin: 'origin-center',
  },
  pop: {
    hover: 'group-hover/solar:animate-solar-pop',
    active: 'animate-solar-pop',
    origin: 'origin-center',
  },
  pulse: {
    hover: 'group-hover/solar:animate-solar-pulse',
    active: 'animate-solar-pulse',
    origin: 'origin-center',
  },
  swing: {
    hover: 'group-hover/solar:animate-solar-swing',
    active: 'animate-solar-swing',
    origin: 'origin-top',
  },
  spin: {
    hover: 'group-hover/solar:animate-solar-spin',
    active: 'animate-solar-spin',
    origin: 'origin-center',
  },
};

const icon = computed(() => SOLAR_ICONS[props.name]);
const reducedMotion = prefersReducedMotion();

const pieceClass = computed(() => {
  if (reducedMotion) return [];
  const motion = MOTIONS[icon.value.motion];
  return [
    '[transform-box:fill-box] motion-reduce:animate-none',
    motion.origin,
    props.active ? motion.active : motion.hover,
  ];
});

const layerClass = layer =>
  icon.value.piece === layer ? pieceClass.value : [];
const pathAttrs = path => (typeof path === 'string' ? { d: path } : path);
</script>

<template>
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    class="group/solar shrink-0"
    aria-hidden="true"
    :data-solar="name"
  >
    <g :class="layerClass('duo')" data-layer="duo">
      <g opacity=".5">
        <path
          v-for="(path, index) in icon.duo"
          :key="index"
          v-bind="pathAttrs(path)"
        />
      </g>
    </g>
    <g :class="layerClass('solid')" data-layer="solid">
      <path
        v-for="(path, index) in icon.solid"
        :key="index"
        v-bind="pathAttrs(path)"
      />
    </g>
  </svg>
</template>
